import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { appContext } from '../../service/AppContext';
import './CategoryPicker.css';

export default function CategoryPicker({ value, onChange, languageCode }) {
    const { breslevRequest } = useContext(appContext);
    const [available, setAvailable] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('');
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const prevLang = useRef(languageCode);

    const selected = useMemo(() => new Set((value || []).map(Number)), [value]);

    const load = async () => {
        setLoading(true);
        const params = languageCode ? { language_code: languageCode } : undefined;
        const res = await breslevRequest({ method: 'GET', route: '/broadcast/categories', params });
        if (res?.status === 200 && Array.isArray(res.data)) {
            setAvailable(res.data);
        } else {
            toast('שגיאה בטעינת קטגוריות');
        }
        setLoading(false);
    };

    useEffect(() => {
        // Category term IDs are per-language — clear the selection when the
        // broadcast language changes (but not on the initial mount).
        if (prevLang.current !== languageCode) {
            prevLang.current = languageCode;
            onChange([]);
        }
        load();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [languageCode]);

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onChange(Array.from(next));
    };

    const createCategory = async () => {
        if (!newName.trim()) return;
        setCreating(true);
        const res = await breslevRequest({
            method: 'POST',
            route: '/broadcast/categories',
            body: { name: newName.trim(), language_code: languageCode },
        });
        setCreating(false);
        if (res?.status === 201 && res.data?.id) {
            const cat = res.data;
            setAvailable(curr => [cat, ...curr]);
            onChange([...(value || []), cat.id]);
            setNewName('');
            toast('הקטגוריה נוצרה');
        } else {
            toast('שגיאה ביצירת קטגוריה');
        }
    };

    const filtered = available.filter(c => !filter || (c.name || '').toLowerCase().includes(filter.toLowerCase()));

    return (
        <div className='category-picker'>
            <div className='cp-row'>
                <input
                    className='cp-search'
                    type='text'
                    placeholder='חיפוש קטגוריה'
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
                {loading && <span className='cp-loading'>טוען...</span>}
            </div>
            <div className='cp-list'>
                {filtered.map(cat => (
                    <label key={cat.id} className={'cp-chip' + (selected.has(cat.id) ? ' cp-chip-on' : '')}>
                        <input
                            type='checkbox'
                            checked={selected.has(cat.id)}
                            onChange={() => toggle(cat.id)}
                        />
                        <span>{cat.name}</span>
                    </label>
                ))}
                {(!filtered.length && !loading) && <div className='cp-empty'>אין תוצאות</div>}
            </div>
            <div className='cp-create'>
                <input
                    className='cp-search'
                    type='text'
                    placeholder='שם קטגוריה חדשה'
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                />
                <button type='button' disabled={creating || !newName.trim()} onClick={createCategory}>
                    {creating ? 'יוצר...' : '+ צור קטגוריה'}
                </button>
            </div>
        </div>
    );
}
